import { useCallback, useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { IMPORTS_API, IMPORTS_API_HOST, JSON_HEADERS } from './constants';
import type { FetchImports, ImportMapping, RegisterImport, UseImportsReturn } from './types';

const useImports = (): UseImportsReturn => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPost } = useFetch({
        apiHost: IMPORTS_API_HOST,
        headers: JSON_HEADERS
    });
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchImportsRaw = useCallback(
        async ({
            externalSource
        }: {
            externalSource: string;
        }): Promise<{ imports: ImportMapping[] } | null> => {
            try {
                setIsLoading(true);

                const params = new URLSearchParams({ externalSource });

                return await withRetry(
                    () =>
                        authenticatedGet<{ imports: ImportMapping[] }>(
                            `${IMPORTS_API}?${params.toString()}`
                        ),
                    CONNECTION_ERROR,
                    () => fetchImportsRaw({ externalSource })
                );
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedGet, withRetry, CONNECTION_ERROR]
    );

    const fetchImports: FetchImports = useCallback(
        async ({ externalSource }) => {
            const response = await fetchImportsRaw({ externalSource });

            return response?.imports ?? null;
        },
        [fetchImportsRaw]
    );

    const registerImport: RegisterImport = useCallback(
        async ({ externalId, externalSource, svmEntityId, svmEntityType }) => {
            try {
                setIsLoading(true);

                return await withRetry(
                    () =>
                        authenticatedPost<ImportMapping>(
                            IMPORTS_API,
                            JSON.stringify({
                                externalId,
                                externalSource,
                                svmEntityId,
                                svmEntityType
                            })
                        ),
                    SAVE_FAILED,
                    () =>
                        registerImport({
                            externalId,
                            externalSource,
                            svmEntityId,
                            svmEntityType
                        })
                );
            } finally {
                setIsLoading(false);
            }
        },
        [authenticatedPost, withRetry, SAVE_FAILED]
    );

    return {
        fetchImports,
        isLoading,
        registerImport
    };
};

export default useImports;
